import {
  AgentStatus,
  AgentType,
  AgentsHealthStatus,
} from "@superblocksteam/shared";
import { isEmpty } from "lodash";
import { createSelector } from "reselect";
import { ROOT } from "store/utils/types";
import {
  controlFlowOverrideFlagSelector,
  selectControlFlowEnabledDynamic,
} from "../apisShared/selectors/controlFlowEnabledDynamic";
import slice from "./slice";
import { isProfileSupported } from "./utils";
import type { AgentWithHealth } from "./client";
import type { Agent } from "@superblocksteam/shared";

export const selectOnPremAgentWithHealths = createSelector(
  slice.selector,
  (state) => {
    const agents = Object.values(state.entities);
    return agents.filter(
      (agentWithHealth) => agentWithHealth.agent.type === AgentType.ONPREMISE,
    );
  },
);

export const selectActiveAgents = (
  agentType: AgentType,
  profileKey: string,
) => {
  return createSelector(
    slice.selector,
    selectControlFlowEnabledDynamic,
    controlFlowOverrideFlagSelector,
    (state, controlFlowEnabled, controlFlowOverrideEnabled) => {
      const agents = Object.values(state.entities).map(
        (agentWithHealth) => agentWithHealth.agent,
      );
      return shuffleAgents(agents).filter((agent) =>
        canExecute({
          targetAgentType: agentType,
          targetEnvironment: profileKey,
          agent,
          controlFlowEnabled,
          controlFlowOverrideEnabled,
        }),
      );
    },
  );
};

export const selectAllAgents = createSelector(slice.selector, (state) => {
  return state.entities;
});

export const isLoadingAgentsHealth = createSelector(slice.selector, (state) => {
  return state.loading?.[ROOT] === true;
});

export const chooseAgents = (
  idToAgentHealth: Record<string, AgentWithHealth>,
  agentType: AgentType,
  profileKey: string,
  controlFlowEnabled: boolean,
  controlFlowOverrideEnabled: boolean,
) => {
  const agents = Object.values(idToAgentHealth).map(
    (agentWithHealth) => agentWithHealth.agent,
  );

  const availableAgents = shuffleAgents(agents).filter((agent) =>
    canExecute({
      targetAgentType: agentType,
      targetEnvironment: profileKey,
      agent,
      controlFlowEnabled,
      controlFlowOverrideEnabled,
    }),
  );
  return availableAgents;
};

export const selectAgentsHealthStatus = (
  agentType: AgentType,
  targetEnvironment: string,
) =>
  createSelector(slice.selector, (state): AgentsHealthStatus => {
    // if no organizationId, we don't know if the agent is active or not
    if (!state.meta[ROOT]?.organizationId) {
      return AgentsHealthStatus.UNKNOWN;
    }

    // if loading and no agents, we don't know if the agent is active or not
    const loading = state.loading[ROOT] === true;
    const noAgents = isEmpty(state.entities);
    if (loading && noAgents) return AgentsHealthStatus.UNKNOWN;

    let hasAgentsForOtherProfiles = false;
    let hasUnreachableAgentsForProfile = false;
    for (const agentWithHealth of Object.values(state.entities)) {
      if (agentWithHealth.agent.type !== agentType) {
        // ignore agents of other types
        continue;
      }
      // If there is no target environment, we don't care about filtering the agents based on the profile, we just check
      // if *any* agent is healthy. This can happen when the user is in a context where there is no profile, like the home page.
      if (
        !targetEnvironment ||
        isProfileSupported(agentWithHealth.agent, targetEnvironment)
      ) {
        if (agentWithHealth.agent.status === AgentStatus.ACTIVE) {
          // if we find an active agent for the current profile, we are good
          return AgentsHealthStatus.HEALTHY;
        } else if (
          agentWithHealth.agent.status === AgentStatus.BROWSER_UNREACHABLE
        ) {
          hasUnreachableAgentsForProfile = true;
        }
      } else if (agentWithHealth.agent.status === AgentStatus.ACTIVE) {
        hasAgentsForOtherProfiles = true;
      }
    }

    if (hasUnreachableAgentsForProfile) {
      return AgentsHealthStatus.NO_REACHABLE_AGENTS;
    } else if (hasAgentsForOtherProfiles) {
      return AgentsHealthStatus.NO_AGENTS_FOR_PROFILE;
    } else {
      return AgentsHealthStatus.NO_AGENTS;
    }
  });

const shuffleAgents = (agents: Agent[]): Agent[] => {
  return agents
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ value }) => value);
};

export const canExecute = ({
  targetAgentType,
  targetEnvironment,
  agent,
  controlFlowEnabled,
  controlFlowOverrideEnabled,
}: {
  targetAgentType: AgentType;
  targetEnvironment: string;
  agent: Agent;
  controlFlowEnabled: boolean;
  controlFlowOverrideEnabled: boolean;
}): boolean => {
  if (agent.status !== AgentStatus.ACTIVE || agent.type !== targetAgentType) {
    return false;
  }
  if (!isProfileSupported(agent, targetEnvironment)) {
    return false;
  }
  if (controlFlowOverrideEnabled && controlFlowEnabled) {
    // technically redundant since controlFlowEnabled is always true if the override is also true
    return true;
  }
  if (!controlFlowEnabled) {
    return false;
  }
  return true;
};
